import { useContext, useState, useEffect, useRef } from 'react';
import {
	Box,
	Flex,
	Heading,
	Text,
	Switch,
	Button,
	Alert,
	AlertIcon,
	AlertTitle,
	AlertDescription,
	Spinner,
	Card,
	CardBody,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import UserContext from 'src/contexts/user/UserContext';
import CustomContainer from 'src/components/common/CustomContainer';
import FusionLoading from 'src/components/common/FusionLoading';
import { toastSuccess, toastError } from 'src/services/toast';
import {
	activateAiVoiceAgent,
	deactivateAiVoiceAgent,
} from 'src/services/account';
import { AiVoiceAgentIcon } from 'src/assets/icons';
import { CheckCircleIcon } from 'src/assets/icons';

type AiVoiceAgentStatus = 'disabled' | 'configuring' | 'active';

const AiVoiceAgent = () => {
	const { account, refreshAccount, user } = useContext(UserContext);
	const navigate = useNavigate();
	const [status, setStatus] = useState<AiVoiceAgentStatus>('disabled');
	const [isLoading, setIsLoading] = useState(false);
	const [isInitialLoading, setIsInitialLoading] = useState(true);
	const [missingFields, setMissingFields] = useState<string[]>([]);
	const hasRefreshed = useRef(false);

	// Refresh account data when component mounts to get latest state
	useEffect(() => {
		if (!hasRefreshed.current) {
			hasRefreshed.current = true;
			refreshAccount().finally(() => {
				setIsInitialLoading(false);
			});
		}
	}, []);

	// Sync status from account
	useEffect(() => {
		if (account) {
			setStatus((account.aiVoiceAgent as AiVoiceAgentStatus) || 'disabled');
		}
	}, [account]);

	// Check for missing required fields
	useEffect(() => {
		if (account) {
			const missing: string[] = [];
			if (!account.name) {
				missing.push('business name');
			}
			if (!account.phone) {
				missing.push('phone');
			}
			if (!account.industry?.name) {
				missing.push('industry');
			}
			setMissingFields(missing);
		}
	}, [account]);

	// Toggle is checked when status is 'configuring' or 'active'
	const isToggleOn = status === 'configuring' || status === 'active';

	const handleToggle = async () => {
		if (isToggleOn) {
			// Deactivate
			setIsLoading(true);
			try {
				const result = await deactivateAiVoiceAgent();
				if (result.success) {
					setStatus('disabled');
					toastSuccess('AI Voice Agent deactivated');
					refreshAccount();
				}
			} catch (error: any) {
				toastError(error.message || 'Failed to deactivate AI Voice Agent');
			} finally {
				setIsLoading(false);
			}
		} else {
			// Activate - always goes to 'configuring' state
			setIsLoading(true);
			try {
				const userEmail = user?.email || '';
				const result = await activateAiVoiceAgent(userEmail);
				if (result.success) {
					setStatus('configuring');
					toastSuccess('AI Voice Agent enabled! We will contact you soon.');
					// Update missing fields if returned
					if (result.missingFields) {
						setMissingFields(result.missingFields);
					}
					refreshAccount();
				}
			} catch (error: any) {
				toastError(error.message || 'Failed to enable AI Voice Agent');
			} finally {
				setIsLoading(false);
			}
		}
	};

	const handleGoToCompany = () => {
		// Pass missing fields as query param to highlight them in the form
		const highlightFields = missingFields
			.map((field) => {
				// Map display names to form field names
				if (field === 'business name') return 'name';
				if (field === 'phone') return 'phone';
				if (field === 'industry') return 'industry';
				return field;
			})
			.join(',');
		navigate(`/account/company?highlight=${highlightFields}`);
	};

	const getToggleSubtext = () => {
		if (status === 'active') return 'Your AI Voice Agent is active and ready';
		if (status === 'configuring') return 'We are configuring your AI Voice Agent';
		return 'Turn on to enable your AI Voice Agent';
	};

	// Show loading state while fetching account data
	if (isInitialLoading) {
		return (
			<Flex justifyContent="center" alignItems="center" w="full" minH="400px">
				<FusionLoading />
			</Flex>
		);
	}

	return (
		<Flex justifyContent="center" w="full">
			<CustomContainer maxW="lg" justifyContent="center">
				<Flex direction="column" gap="24px">
					{/* Header */}
					<Flex direction="column" gap="8px" alignItems="center" textAlign="center">
						<Box
							p={4}
							borderRadius="full"
							bg="orange.50"
							display="flex"
							alignItems="center"
							justifyContent="center"
						>
							<AiVoiceAgentIcon width="48px" height="48px" color="#F04D24" />
						</Box>
						<Heading size="lg" mt={2}>AI Voice Agent</Heading>
						<Text color="gray.600" maxW="md">
							Enable your AI-powered voice assistant to handle customer calls,
							answer questions, and provide support 24/7.
						</Text>
					</Flex>

					{/* Toggle Card */}
					<Card variant="outline" shadow="sm">
						<CardBody>
							<Flex justify="space-between" align="center">
								<Flex direction="column" gap="4px">
									<Text fontWeight="semibold" fontSize="lg">
										Enable AI Voice Agent
									</Text>
									<Text color="gray.500" fontSize="sm">
										{getToggleSubtext()}
									</Text>
								</Flex>
								<Switch
									size="lg"
									colorScheme="orange"
									isChecked={isToggleOn}
									onChange={handleToggle}
									isDisabled={isLoading || status === 'active'}
								/>
							</Flex>
						</CardBody>
					</Card>

					{/* Loading State */}
					{isLoading && (
						<Flex justify="center" align="center" gap={3} py={4}>
							<Spinner color="orange.500" />
							<Text color="gray.600">
								{isToggleOn ? 'Deactivating...' : 'Enabling...'}
							</Text>
						</Flex>
					)}

					{/* Missing Fields Warning - show when toggle is on but fields are missing */}
					{isToggleOn && missingFields.length > 0 && !isLoading && (
						<Alert
							status="warning"
							variant="subtle"
							flexDirection="column"
							alignItems="flex-start"
							borderRadius="md"
							py={4}
						>
							<Flex align="center" gap={2} mb={2}>
								<AlertIcon />
								<AlertTitle>Missing Required Information</AlertTitle>
							</Flex>
							<AlertDescription mb={3}>
								To complete the setup, please fill in the following field(s):{' '}
								<Text as="span" fontWeight="semibold">
									{missingFields.join(', ')}
								</Text>
							</AlertDescription>
							<Button
								size="sm"
								colorScheme="orange"
								variant="outline"
								onClick={handleGoToCompany}
							>
								Complete Company Profile
							</Button>
						</Alert>
					)}

				{/* Configuring State - Blue alert with loading (only if no missing fields) */}
				{status === 'configuring' && missingFields.length === 0 && !isLoading && (
						<Alert
							status="info"
							variant="subtle"
							flexDirection="column"
							alignItems="flex-start"
							borderRadius="md"
							py={4}
						>
							<Flex align="center" gap={2} mb={2}>
								<Spinner size="sm" color="blue.500" mr={1} />
								<AlertTitle>Setup In Progress</AlertTitle>
							</Flex>
							<AlertDescription mb={3}>
								We are configuring your AI Voice Agent phone number. You will receive
								an email notification when everything is ready and your agent is
								live!
							</AlertDescription>
							<Text fontSize="sm" color="blue.700" mb={2}>
								No worries, while you wait you can go ahead and create a campaign:
							</Text>
							<Button
								size="sm"
								colorScheme="blue"
								variant="outline"
								onClick={() => navigate('/projects/campaigns/new')}
							>
								Create Campaign
							</Button>
						</Alert>
					)}

					{/* Active State - Green alert */}
					{status === 'active' && !isLoading && (
						<Alert
							status="success"
							variant="subtle"
							flexDirection="column"
							alignItems="flex-start"
							borderRadius="md"
							py={4}
						>
							<Flex align="center" gap={2} mb={2}>
								<AlertIcon />
								<AlertTitle>AI Voice Agent Active</AlertTitle>
							</Flex>
							<AlertDescription>
								Your AI Voice Agent is live and ready to handle customer calls.
								Toggle the switch above to disable it.
							</AlertDescription>
						</Alert>
					)}

					{/* Features List */}
					<Card variant="outline" shadow="sm">
						<CardBody>
							<Text fontWeight="semibold" mb={3}>
								What your AI Voice Agent can do:
							</Text>
							<Flex direction="column" gap={2}>
								<Flex align="center" gap={2}>
									<CheckCircleIcon color="#38A169" width="16px" height="16px" />
									<Text fontSize="sm">Answer customer calls 24/7</Text>
								</Flex>
								<Flex align="center" gap={2}>
									<CheckCircleIcon color="#38A169" width="16px" height="16px" />
									<Text fontSize="sm">Provide business information</Text>
								</Flex>
								<Flex align="center" gap={2}>
									<CheckCircleIcon color="#38A169" width="16px" height="16px" />
									<Text fontSize="sm">Schedule appointments</Text>
								</Flex>
								<Flex align="center" gap={2}>
									<CheckCircleIcon color="#38A169" width="16px" height="16px" />
									<Text fontSize="sm">Handle frequently asked questions</Text>
								</Flex>
								<Flex align="center" gap={2}>
									<CheckCircleIcon color="#38A169" width="16px" height="16px" />
									<Text fontSize="sm">Transfer calls when needed</Text>
								</Flex>
							</Flex>
						</CardBody>
					</Card>
				</Flex>
			</CustomContainer>
		</Flex>
	);
};

export default AiVoiceAgent;
